import React, { useEffect, useState } from 'react';
import { Button, Modal, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';
import Draggable from 'react-draggable';
import { getAllClientDropdown } from '../../../../Redux/client/action';
import { getFacilityDropdown } from '../../../../Redux/user/action';
import { searchPropagationJob, schedulePropagation } from '../../../../Redux/recordPropagation/action';
import { downloadFullS3File } from '../../../../Redux/sftpClient/action';
import FileViewerModal from '../../../common/FileViewerModal';

const formatFileSize = (bytes) => {
    const num = Number(bytes);
    if (!num) return null;
    if (num >= 1024 * 1024) return `${(num / (1024 * 1024)).toFixed(2)} MB`;
    if (num >= 1024) return `${(num / 1024).toFixed(1)} KB`;
    return `${num} B`;
};

const TARGET_JOB_STATUS_OPTIONS = [
    { label: 'Imported', value: 'imported' },
    { label: 'QA Review', value: 'qa_review' },
    { label: 'Snipping', value: 'snipping' },
    { label: 'Ready To Output Generate', value: 'ready_to_output_generate' },
];

const ROLE_META = {
    source: { label: 'Source Job', icon: 'bi-box-arrow-up', accent: '#3f7cac', badge: 'primary', hint: "The job you're copying records from" },
    target: { label: 'Target Job', icon: 'bi-box-arrow-in-down', accent: '#2f9e44', badge: 'success', hint: "The job you're copying records into" },
};

const STEPS = [
    { key: 'search', label: 'Search & Select' },
    { key: 'confirm', label: 'Confirm & Schedule' },
];

const emptyJobState = { jobId: '', loading: false, error: '', jobs: [], job: null, searched: false };

const selectStyles = {
    control: (styles) => ({
        ...styles,
        backgroundColor: "hsl(204deg 33.33% 97.06%)",
        borderStyle: "none",
        boxShadow: "none",
        borderRadius: "10px",
        minHeight: "42px",
    }),
};

const StepIndicator = ({ step }) => {
    const activeIndex = STEPS.findIndex(s => s.key === step);
    return (
        <div className="d-flex align-items-center justify-content-center py-3 mb-4" style={{ borderBottom: '1px solid #e9ecef' }}>
            {STEPS.map((s, idx) => (
                <React.Fragment key={s.key}>
                    <div className="d-flex align-items-center gap-2">
                        <span
                            className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                            style={{
                                width: 24,
                                height: 24,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                backgroundColor: idx <= activeIndex ? '#3f7cac' : '#e9ecef',
                                color: idx <= activeIndex ? '#fff' : '#6c757d',
                            }}
                        >
                            {idx + 1}
                        </span>
                        <span
                            className="small"
                            style={{ fontWeight: 600, color: idx <= activeIndex ? '#212529' : '#adb5bd' }}
                        >
                            {s.label}
                        </span>
                    </div>
                    {idx < STEPS.length - 1 &&
                        <div className="mx-3" style={{ width: 40, height: 1, backgroundColor: '#dee2e6' }} />}
                </React.Fragment>
            ))}
        </div>
    );
};

const JobCard = ({ job, accent = '#3f7cac', badgeVariant = 'primary', icon, onView, viewLoading }) => {
    if (!job) return null;
    const jobId = job?.id ?? job?.pk;
    const fields = [
        { label: 'Job ID', value: job?.unique ?? jobId },
        { label: 'Client', value: job?.client_name },
        { label: 'Facility', value: job?.facility_name },
        { label: 'Payer', value: job?.payer_name },
        { label: 'Deposit Date', value: job?.deposit_date },
        { label: 'Lockbox', value: job?.lockbox_name },
        { label: 'Lockbox File', value: job?.lockbox_file_name },
        { label: 'Check #', value: job?.check_number },
        { label: 'Claims', value: job?.no_of_claims },
        { label: 'Type', value: job?.transaction_type },
        { label: 'Pages', value: job?.number_of_pages },
        { label: 'File Size', value: formatFileSize(job?.file_size) },
    ].filter(f => f.value != null && f.value !== '');

    return (
        <div className="rounded-3 bg-white overflow-hidden" style={{ border: '1px solid #e9ecef', borderLeft: `4px solid ${accent}` }}>
            <div
                className="d-flex justify-content-between align-items-center gap-2 px-3 py-2"
                style={{ backgroundColor: '#fbfcfd', borderBottom: '1px solid #f1f3f5' }}
            >
                <div className="d-flex align-items-center gap-2 text-truncate">
                    {icon && <i className={`bi ${icon} text-muted`}></i>}
                    <strong className="text-truncate">{job?.unique || `Job #${jobId}`}</strong>
                </div>
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    {job?.split_image_path && (
                        <button
                            type="button"
                            title="View file"
                            className="btn btn-sm btn-light d-inline-flex align-items-center justify-content-center"
                            style={{ width: 28, height: 28, padding: 0 }}
                            disabled={viewLoading}
                            onClick={() => onView && onView(job.split_image_path)}
                        >
                            {viewLoading ? <Spinner animation="border" size="sm" /> : <i className="bi bi-eye"></i>}
                        </button>
                    )}
                    {job?.status && (
                        <span className={`badge bg-${badgeVariant} text-uppercase`} style={{ fontSize: '0.7rem' }}>
                            {job.status.replace(/_/g, ' ')}
                        </span>
                    )}
                </div>
            </div>
            <div className="px-3 py-1">
                {fields.map((f, idx) => (
                    <div
                        key={f.label}
                        className="d-flex justify-content-between align-items-start gap-3 py-2 small"
                        style={idx < fields.length - 1 ? { borderBottom: '1px dashed #f1f3f5' } : undefined}
                    >
                        <span className="text-muted flex-shrink-0">{f.label}</span>
                        <span className="text-end text-truncate" style={{ fontWeight: 500 }}>{f.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const JobSearchPanel = ({ role, disabled, state, setState, client, facility, lockboxName, onViewFile, viewFileLoading }) => {
    const meta = ROLE_META[role];

    const handleSearch = () => {
        if (!state.jobId?.trim()) {
            setState(prev => ({ ...prev, error: 'Enter a Job ID to search', job: null, jobs: [] }));
            return;
        }
        setState(prev => ({ ...prev, loading: true, error: '', job: null, jobs: [], searched: true }));
        searchPropagationJob({
            client: client?.value,
            facility: facility?.value,
            unique: state.jobId.trim(),
            lockbox_name: lockboxName?.trim() || '',
        })((response) => {
            const jobs = Array.isArray(response?.data)
                ? response.data
                : (Array.isArray(response?.data?.results) ? response.data.results : (response?.data ? [response.data] : []));
            if (response?.status && jobs.length > 0) {
                setState(prev => ({ ...prev, loading: false, jobs, job: jobs.length === 1 ? jobs[0] : null, error: '' }));
            } else {
                setState(prev => ({ ...prev, loading: false, jobs: [], job: null, error: response?.message || 'No matching job found' }));
            }
        });
    };

    const handleSelectJob = (job) => setState(prev => ({ ...prev, job }));
    const handleChangeSelection = () => setState(prev => ({ ...prev, job: null }));

    return (
        <div className="border rounded-3 h-100 bg-white overflow-hidden">
            <div className="d-flex align-items-center gap-2 px-3 py-3 border-bottom" style={{ backgroundColor: '#fbfcfd' }}>
                <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{ width: 32, height: 32, backgroundColor: `${meta.accent}1a` }}
                >
                    <i className={`bi ${meta.icon}`} style={{ color: meta.accent, fontSize: '0.95rem' }}></i>
                </span>
                <div>
                    <div style={{ fontWeight: 600 }}>{meta.label}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>{meta.hint}</div>
                </div>
            </div>
            <div className="p-3">
                <Form.Label className="text-muted small mb-1">Job ID</Form.Label>
                <div className="d-flex gap-2 mb-2">
                    <Form.Control
                        placeholder="Enter Job ID"
                        value={state.jobId}
                        disabled={disabled}
                        onChange={(e) => setState(prev => ({ ...prev, jobId: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
                        className="form-control-solid"
                    />
                    <Button
                        variant="light"
                        className="btn-active-primary flex-shrink-0"
                        style={{ minWidth: 100 }}
                        disabled={disabled || state.loading}
                        onClick={handleSearch}
                    >
                        {state.loading
                            ? <Spinner animation="border" size="sm" />
                            : <><i className="bi bi-search me-2"></i>Search</>}
                    </Button>
                </div>

                {state.error && (
                    <div
                        className="d-flex align-items-center gap-2 rounded-3 p-2 mb-2"
                        style={{ backgroundColor: '#fdf2f2', border: '1px solid #f6d5d5' }}
                    >
                        <span
                            className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                            style={{ width: 26, height: 26, backgroundColor: '#f6d5d5' }}
                        >
                            <i className="bi bi-exclamation-triangle-fill" style={{ color: '#b3312c', fontSize: '0.75rem' }}></i>
                        </span>
                        <span className="small" style={{ color: '#8a2622' }}>{state.error}</span>
                    </div>
                )}

                {state.job && (
                    <div className="mt-2">
                        <JobCard job={state.job} accent={meta.accent} badgeVariant={meta.badge} icon={meta.icon} onView={onViewFile} viewLoading={viewFileLoading} />
                        {state.jobs.length > 1 && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0 mt-2"
                                onClick={handleChangeSelection}
                            >
                                <i className="bi bi-arrow-left me-1"></i>
                                Choose a different job ({state.jobs.length} found)
                            </button>
                        )}
                    </div>
                )}

                {!state.job && state.jobs.length > 0 && (
                    <div className="mt-2">
                        <div className="text-muted small mb-2">{state.jobs.length} jobs found — select one:</div>
                        <div className="list-group" style={{ maxHeight: 220, overflowY: 'auto' }}>
                            {state.jobs.map((job) => {
                                const jid = job?.id ?? job?.pk ?? job?.unique;
                                return (
                                    <button
                                        key={jid}
                                        type="button"
                                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2"
                                        onClick={() => handleSelectJob(job)}
                                    >
                                        <span>{job?.unique || `Job #${jid}`}</span>
                                        <i className="bi bi-chevron-right text-muted"></i>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {!state.job && state.jobs.length === 0 && !state.loading && !state.error && (
                    <div className="text-muted small mt-1">
                        {state.searched ? 'No job found for that ID.' : 'Enter a Job ID and search to select this job.'}
                    </div>
                )}
            </div>
        </div>
    );
};

const CreatePropagateRequestModal = ({ show, onClose, onSuccess }) => {
    const [step, setStep] = useState('search');
    const [client, setClient] = useState(null);
    const [facility, setFacility] = useState(null);
    const [lockboxName, setLockboxName] = useState('');
    const [clientOptions, setClientOptions] = useState([]);
    const [facilityOptions, setFacilityOptions] = useState([]);
    const [source, setSource] = useState(emptyJobState);
    const [target, setTarget] = useState(emptyJobState);
    const [remarks, setRemarks] = useState('');
    const [remarksError, setRemarksError] = useState('');
    const [targetJobStatus, setTargetJobStatus] = useState(TARGET_JOB_STATUS_OPTIONS[0]);
    const [targetJobStatusError, setTargetJobStatusError] = useState('');
    const [includeSnippingPayload, setIncludeSnippingPayload] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [fileViewer, setFileViewer] = useState({ show: false, url: '' });
    const [loadingFilePath, setLoadingFilePath] = useState('');

    const handleViewFile = (path) => {
        if (!path || loadingFilePath) return;
        setLoadingFilePath(path);
        downloadFullS3File({ file_path: path })((response) => {
            setLoadingFilePath('');
            if (response?.status && response?.data?.signed_url) {
                setFileViewer({ show: true, url: response.data.signed_url });
            } else {
                Swal.fire({
                    title: 'Error',
                    text: response?.message || 'Failed to load file preview.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#6ba2b6',
                });
            }
        });
    };

    useEffect(() => {
        if (show) {
            getAllClientDropdown()((response) => {
                setClientOptions(response?.data || []);
            });
        }
    }, [show]);

    useEffect(() => {
        setFacility(null);
        setFacilityOptions([]);
        setSource(emptyJobState);
        setTarget(emptyJobState);

        if (client?.value) {
            getFacilityDropdown([client.value])((response) => {
                if (response?.status) {
                    const options = response.data?.facilities?.flatMap(i => i.facilities) || [];
                    setFacilityOptions(options);
                }
            });
        }
    }, [client]);

    useEffect(() => {
        setSource(emptyJobState);
        setTarget(emptyJobState);
    }, [facility]);

    const resetAll = () => {
        setStep('search');
        setClient(null);
        setFacility(null);
        setLockboxName('');
        setFacilityOptions([]);
        setSource(emptyJobState);
        setTarget(emptyJobState);
        setRemarks('');
        setRemarksError('');
        setTargetJobStatus(TARGET_JOB_STATUS_OPTIONS[0]);
        setTargetJobStatusError('');
        setIncludeSnippingPayload(false);
        setSubmitting(false);
    };

    const handleClose = () => {
        if (submitting) return;
        resetAll();
        onClose();
    };

    const canProceedToConfirm = !!client && !!facility && !!source.job && !!target.job;

    const handleProceedToConfirm = () => {
        if (!canProceedToConfirm) return;
        setStep('confirm');
    };

    const handleConfirmSubmit = () => {
        if (submitting) return;
        let hasError = false;
        if (!remarks?.trim()) {
            setRemarksError('Remarks are required');
            hasError = true;
        } else {
            setRemarksError('');
        }
        if (!targetJobStatus?.value) {
            setTargetJobStatusError('Target Job Status is required');
            hasError = true;
        } else {
            setTargetJobStatusError('');
        }
        if (hasError) return;

        setSubmitting(true);

        const sourceJobId = source.job?.id ?? source.job?.pk;
        const targetJobId = target.job?.id ?? target.job?.pk;

        schedulePropagation({
            client: client?.value,
            facility: facility?.value,
            source_job: sourceJobId,
            propagate_job: targetJobId,
            remarks: remarks.trim(),
            target_job_status: targetJobStatus?.value,
            include_snipping_payload: includeSnippingPayload,
        })((response) => {
            setSubmitting(false);
            if (response?.status) {
                Swal.fire({
                    title: 'Success',
                    text: response?.message || 'Propagate request scheduled successfully.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#6ba2b6',
                }).then(() => {
                    resetAll();
                    onClose();
                    onSuccess && onSuccess();
                });
            } else {
                Swal.fire({
                    title: 'Error',
                    text: response?.message || 'Failed to schedule propagation. Please try again.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#6ba2b6',
                });
            }
        });
    };

    return (
        <>
        <Modal show={show} onHide={handleClose} size="xl" centered scrollable backdrop="static" keyboard={!submitting}>
            <Modal.Header closeButton={!submitting}>
                <Modal.Title className="d-flex align-items-center gap-3">
                    <span
                        className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                        style={{ width: 40, height: 40, backgroundColor: '#eaf3fb' }}
                    >
                        <i className="bi bi-arrow-left-right" style={{ color: '#3f7cac', fontSize: '1.1rem' }}></i>
                    </span>
                    <span>
                        <div>Create Propagate Request</div>
                        <div className="text-muted small" style={{ fontWeight: 400 }}>Copy a job's records into another job</div>
                    </span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ backgroundColor: '#f8f9fa' }}>
                <div className="mx-auto" style={{ maxWidth: 1200 }}>
                    <StepIndicator step={step} />

                    {step === 'search' && (
                        <>
                            <div className="bg-white border rounded-3 p-3 p-md-4 mb-4">
                                <div className="row g-3">
                                    <div className="col-12 col-md-4">
                                        <Form.Label style={{ fontWeight: 600 }}>Client <span className="text-danger">*</span></Form.Label>
                                        <Select
                                            options={clientOptions}
                                            value={client}
                                            onChange={setClient}
                                            placeholder="Select Client"
                                            isClearable
                                            styles={selectStyles}
                                        />
                                    </div>
                                    <div className="col-12 col-md-4">
                                        <Form.Label style={{ fontWeight: 600 }}>Facility <span className="text-danger">*</span></Form.Label>
                                        <Select
                                            options={facilityOptions}
                                            value={facility}
                                            onChange={setFacility}
                                            placeholder="Select Facility"
                                            isClearable
                                            isDisabled={!client}
                                            styles={selectStyles}
                                        />
                                    </div>
                                    <div className="col-12 col-md-4">
                                        <Form.Label style={{ fontWeight: 600 }}>
                                            Lockbox Name <span className="text-muted" style={{ fontWeight: 400 }}></span>
                                        </Form.Label>
                                        <Form.Control
                                            placeholder="Enter Lockbox Name"
                                            value={lockboxName}
                                            onChange={(e) => setLockboxName(e.target.value)}
                                            className="form-control-solid"
                                        />
                                    </div>
                                </div>

                                {(!client || !facility) && (
                                    <div className="d-flex align-items-center gap-2 text-muted small mt-3">
                                        <i className="bi bi-info-circle"></i>
                                        Select a Client and Facility to search for Source and Target jobs.
                                    </div>
                                )}
                            </div>

                            <div className="d-flex flex-column flex-lg-row align-items-stretch gap-3">
                                <div className="flex-fill" style={{ minWidth: 0 }}>
                                    <JobSearchPanel
                                        role="source"
                                        disabled={!client || !facility}
                                        state={source}
                                        setState={setSource}
                                        client={client}
                                        facility={facility}
                                        lockboxName={lockboxName}
                                        onViewFile={handleViewFile}
                                        viewFileLoading={!!loadingFilePath && loadingFilePath === source.job?.split_image_path}
                                    />
                                </div>
                                <div className="d-flex align-items-center justify-content-center flex-shrink-0">
                                    <span
                                        className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                        style={{ width: 36, height: 36, backgroundColor: '#eaf3fb' }}
                                    >
                                        <i className="bi bi-arrow-right d-none d-lg-inline" style={{ color: '#3f7cac', fontSize: '1rem' }}></i>
                                        <i className="bi bi-arrow-down d-inline d-lg-none" style={{ color: '#3f7cac', fontSize: '1rem' }}></i>
                                    </span>
                                </div>
                                <div className="flex-fill" style={{ minWidth: 0 }}>
                                    <JobSearchPanel
                                        role="target"
                                        disabled={!client || !facility}
                                        state={target}
                                        setState={setTarget}
                                        client={client}
                                        facility={facility}
                                        lockboxName={lockboxName}
                                        onViewFile={handleViewFile}
                                        viewFileLoading={!!loadingFilePath && loadingFilePath === target.job?.split_image_path}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {step === 'confirm' && (
                        <div className="row justify-content-center">
                            <div className="col-12 col-lg-9">
                                <div className="row g-3 mb-4">
                                    <div className="col-12 col-md-6">
                                        <div className="text-muted small mb-2" style={{ fontWeight: 600 }}>Source Job</div>
                                        <JobCard job={source.job} accent={ROLE_META.source.accent} badgeVariant={ROLE_META.source.badge} icon={ROLE_META.source.icon} onView={handleViewFile} viewLoading={!!loadingFilePath && loadingFilePath === source.job?.split_image_path} />
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <div className="text-muted small mb-2" style={{ fontWeight: 600 }}>Target Job</div>
                                        <JobCard job={target.job} accent={ROLE_META.target.accent} badgeVariant={ROLE_META.target.badge} icon={ROLE_META.target.icon} onView={handleViewFile} viewLoading={!!loadingFilePath && loadingFilePath === target.job?.split_image_path} />
                                    </div>
                                </div>

                                <div className="bg-white border rounded-3 p-3 p-md-4">
                                    <div className="mb-3" style={{ fontWeight: 600 }}>Request Details</div>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontWeight: 600 }}>Remarks <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={remarks}
                                            onChange={(e) => { setRemarks(e.target.value); if (remarksError) setRemarksError(''); }}
                                            isInvalid={!!remarksError}
                                            className="form-control-solid"
                                            placeholder="Enter remarks"
                                            disabled={submitting}
                                        />
                                        <Form.Control.Feedback type="invalid">{remarksError}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: 600 }}>
                                            Target Job Status <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Select
                                            options={TARGET_JOB_STATUS_OPTIONS}
                                            value={targetJobStatus}
                                            onChange={(option) => { setTargetJobStatus(option); if (targetJobStatusError) setTargetJobStatusError(''); }}
                                            placeholder="Select Target Job Status"
                                            isDisabled={submitting}
                                            styles={selectStyles}
                                        />
                                        {targetJobStatusError && (
                                            <div className="text-danger small mt-1">{targetJobStatusError}</div>
                                        )}
                                    </Form.Group>
                                    <Form.Group className="mt-3">
                                        <Form.Check
                                            type="switch"
                                            id="include-snipping-payload"
                                            label="Propagate Snipping Payload"
                                            checked={includeSnippingPayload}
                                            onChange={(e) => setIncludeSnippingPayload(e.target.checked)}
                                            disabled={submitting}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="d-flex align-items-center justify-content-between">
                {step === 'search' && (
                    <>
                        <div className="text-muted small">
                            {!canProceedToConfirm && 'Select Client, Facility, and both jobs to continue.'}
                        </div>
                        <div className="d-flex gap-2">
                            <Button variant="light" onClick={handleClose}>Cancel</Button>
                            <Button
                                className="btn btn-light btn-active-primary"
                                disabled={!canProceedToConfirm}
                                onClick={handleProceedToConfirm}
                            >
                                Create Propagate Scheduler
                                <i className="bi bi-arrow-right ms-2"></i>
                            </Button>
                        </div>
                    </>
                )}
                {step === 'confirm' && (
                    <>
                        <div />
                        <div className="d-flex gap-2">
                            <Button
                                variant="light"
                                disabled={submitting}
                                onClick={() => {
                                    setRemarks('');
                                    setRemarksError('');
                                    setStep('search');
                                }}
                            >
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                            </Button>
                            <Button
                                className="btn btn-light btn-active-primary"
                                disabled={submitting}
                                onClick={handleConfirmSubmit}
                            >
                                {submitting
                                    ? <><Spinner animation="border" size="sm" className="me-2" />Scheduling...</>
                                    : <>Schedule Propagation<i className="bi bi-check2 ms-2"></i></>
                                }
                            </Button>
                        </div>
                    </>
                )}
            </Modal.Footer>
        </Modal>
        {fileViewer.show && (
            <Draggable>
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        zIndex: 1400,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    <div style={{ pointerEvents: 'auto' }}>
                        <FileViewerModal
                            fileUrl={fileViewer.url}
                            showFileView={fileViewer.show}
                            setShowFileView={(val) => setFileViewer(prev => ({ ...prev, show: val }))}
                            isneedFullScreenButton={true}
                        />
                    </div>
                </div>
            </Draggable>
        )}
        </>
    );
};

export default CreatePropagateRequestModal;
